import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { AtSign, Check, Send, Square } from "lucide-react";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import {
  buildMentionRegex,
  lastMention,
  mentionAtCaret,
  splitMentions,
} from "@/shared/utils/mentions";

// Both layers must share these exactly, or the highlight drifts off the text.
const BOX_CLASS =
  "w-full px-3 py-3 pr-12 text-sm leading-6 whitespace-pre-wrap break-words";
const BOX_STYLE = { lineHeight: "1.5" };

/** prompt-kit style prompt input: auto-resizing textarea with a send action. */
const PromptInput = memo(
  ({
    input,
    setInput,
    onSend,
    disabled = false,
    // While the agent runs, the send button becomes a stop button.
    isGenerating = false,
    onStop,
    placeholder = "Ask anything about this account...",
    mentions = [],
    knownMentions = [],
    onMentionChange,
    hint,
  }) => {
    const textareaRef = useRef(null);
    const mirrorRef = useRef(null);
    const [highlightedMention, setHighlightedMention] = useState(0);
    const [selectionStart, setSelectionStart] = useState(input.length);

    // Tags already typed stay highlighted even once the picker stops offering
    // them, so the parent can pass the full list separately from the open list.
    const highlightable = knownMentions.length ? knownMentions : mentions;
    const mentionRegex = useMemo(
      () => buildMentionRegex(highlightable.map((mention) => mention.name)),
      [highlightable],
    );

    const activeMention = mentionAtCaret(input, selectionStart);
    const mentionQuery = activeMention?.query || "";
    const filteredMentions = activeMention
      ? mentions
          .filter((mention) =>
            mention.name.toLowerCase().includes(mentionQuery),
          )
          .slice(0, 7)
      : [];

    // The typed text is the source of truth for what is tagged.
    const taggedName = useMemo(
      () => lastMention(input, mentionRegex),
      [input, mentionRegex],
    );

    useEffect(() => {
      if (!onMentionChange) return;
      onMentionChange(
        highlightable.find((mention) => mention.name === taggedName) || null,
      );
    }, [taggedName, highlightable, onMentionChange]);

    useEffect(() => {
      setHighlightedMention(0);
    }, [mentionQuery]);

    useEffect(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = "auto";
      const scrollHeight = el.scrollHeight;
      const height = Math.min(scrollHeight, 120);
      el.style.height = `${height}px`;
      el.style.overflowY = scrollHeight > 120 ? "auto" : "hidden";
      if (mirrorRef.current) mirrorRef.current.style.height = `${height}px`;
    }, [input]);

    const selectMention = (mention) => {
      if (!activeMention) return;
      const before = input.slice(0, activeMention.start);
      const after = input.slice(activeMention.end);
      const tag = mention.name;
      const separator = !after || !/^\s/.test(after) ? " " : "";
      const nextCaret = before.length + tag.length + separator.length;
      setInput(`${before}${tag}${separator}${after}`);
      setSelectionStart(nextCaret);
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
        textareaRef.current?.setSelectionRange(nextCaret, nextCaret);
      });
    };

    const handleKeyDown = (e) => {
      if (filteredMentions.length) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          setHighlightedMention((current) =>
            e.key === "ArrowDown"
              ? (current + 1) % filteredMentions.length
              : (current - 1 + filteredMentions.length) %
                filteredMentions.length,
          );
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          selectMention(filteredMentions[highlightedMention]);
          return;
        }
      }
      // A tag deletes as one unit, not character by character.
      if (e.key === "Backspace" && mentionRegex && !e.repeat) {
        const el = e.target;
        if (el.selectionStart === el.selectionEnd) {
          const caret = el.selectionStart;
          const before = input.slice(0, caret);
          const trailing = before.match(/\s$/) ? 1 : 0;
          for (const match of before.matchAll(new RegExp(mentionRegex))) {
            if (match.index + match[0].length + trailing === caret) {
              e.preventDefault();
              setInput(input.slice(0, match.index) + input.slice(caret));
              requestAnimationFrame(() => {
                el.setSelectionRange(match.index, match.index);
              });
              return;
            }
          }
        }
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!disabled && !isGenerating) onSend();
      }
    };

    // The "@" is kept but rendered invisible: dropping the character outright
    // would shorten the mirror and slide the highlight off the real text. Its
    // reserved width reads as the tag's left padding.
    const highlighted = useMemo(() => {
      if (!mentionRegex) return input;
      return splitMentions(input, mentionRegex).map((part) =>
        typeof part === "string" ? (
          part
        ) : (
          <span
            key={`${part.index}-${part.name}`}
            className="text-secondary"
            style={{ WebkitTextStroke: "0.3px currentColor" }}
          >
            {part.name}
          </span>
        ),
      );
    }, [input, mentionRegex]);

    return (
      <div className="relative rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-2 shadow-[0_10px_30px_rgba(24,29,39,0.06)] transition focus-within:border-secondary/50 focus-within:shadow-[0_14px_36px_rgba(32,124,151,0.10)]">
        {!!filteredMentions.length && (
          <div className="absolute bottom-[calc(100%+8px)] left-0 z-30 w-full max-w-sm overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1.5 shadow-xl">
            <div className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">
              Campaigns
            </div>
            {filteredMentions.map((mention, index) => (
              <button
                key={mention.id}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectMention(mention)}
                className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition ${
                  highlightedMention === index
                    ? "bg-secondary-light-transparent text-secondary font-medium"
                    : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                    highlightedMention === index
                      ? "bg-secondary text-white"
                      : "bg-secondary-light-transparent text-secondary"
                  }`}
                >
                  <AtSign size={14} />
                </span>
                <span className="truncate font-medium">{mention.name}</span>
                {highlightedMention === index && (
                  <Check size={14} className="ml-auto shrink-0 text-secondary" />
                )}
              </button>
            ))}
          </div>
        )}
        <div className="relative">
          {mentionRegex && (
            <div
              ref={mirrorRef}
              aria-hidden="true"
              className={`${BOX_CLASS} pointer-events-none absolute inset-0 overflow-hidden text-gray-800 dark:text-gray-100`}
              style={BOX_STYLE}
            >
              {highlighted}
            </div>
          )}
          <textarea
            ref={textareaRef}
            name="conversation-prompt"
            className={`${BOX_CLASS} relative min-h-16 resize-none rounded-xl bg-transparent outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 ${
              mentionRegex ? "text-transparent caret-gray-800 dark:caret-gray-100" : "text-gray-800 dark:text-gray-100"
            }`}
            placeholder={placeholder}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setSelectionStart(e.target.selectionStart);
            }}
            onSelect={(e) => setSelectionStart(e.currentTarget.selectionStart)}
            onKeyDown={handleKeyDown}
            onScroll={(e) => {
              if (mirrorRef.current) {
                mirrorRef.current.scrollTop = e.target.scrollTop;
              }
            }}
            disabled={disabled}
            rows={1}
            style={BOX_STYLE}
          />
        </div>
        {hint && (
          <div className="pointer-events-none px-3 pb-1 text-[11px] text-gray-400 dark:text-gray-500">
            {hint}
          </div>
        )}
        {isGenerating && onStop ? (
          <PrimaryButton
            type="button"
            onClick={onStop}
            startContent={<Square size={12} className="fill-white text-white" />}
            className="absolute bottom-3 right-3 !p-2 !h-8 !w-8 !min-w-0 flex justify-center items-center shrink-0 shadow-none"
            aria-label="Stop response"
            title="Stop response"
          >
            {""}
          </PrimaryButton>
        ) : (
          <PrimaryButton
            type="button"
            onClick={() => onSend()}
            disabled={disabled || !input.trim()}
            startContent={<Send size={14} className="text-white" />}
            className="absolute bottom-3 right-3 !p-2 !h-8 !w-8 !min-w-0 flex justify-center items-center shrink-0 shadow-none"
            aria-label="Send message"
          >
            {""}
          </PrimaryButton>
        )}
      </div>
    );
  },
);

export default PromptInput;
